import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../utils/axios";
import { API_ROUTES } from "./routes";
import { showToast } from "../components/ui/toast";

export const useGetNetworksQuery = () => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_NETWORKS],
    queryFn: () => api.get(API_ROUTES.GET_NETWORKS),
  });

  return { data: data?.data?.data ?? [], isPending, isError, error };
};

export const useGetNetworkByIdQuery = (id) => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_NETWORKS, id],
    queryFn: () => api.get(`${API_ROUTES.GET_NETWORKS}/${id}`),
    enabled: !!id,
  });

  return { data: data?.data?.data ?? null, isPending, isError, error };
};

export const useCreateNetworkMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (data) => api.post(API_ROUTES.GET_NETWORKS, data),
      onSuccess: () => {
        showToast("success", "Network created successfully");
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [API_ROUTES.GET_NETWORKS],
        });
      },
    });

  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

export const useUpdateNetworkMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: ({ id, ...data }) =>
        api.put(`${API_ROUTES.GET_NETWORKS}/${id}`, data),
      onSuccess: () => {
        showToast("success", "Network updated successfully");
      },
      onSettled: (_, __, variables) => {
        queryClient.invalidateQueries({
          queryKey: [API_ROUTES.GET_NETWORKS],
        });
        if (variables?.id) {
          queryClient.invalidateQueries({
            queryKey: [API_ROUTES.GET_NETWORKS, variables.id],
          });
        }
      },
    });

  return { mutate, isError, error, isLoading, isSuccess, isPending };
};

export const useDeleteNetworkMutation = () => {
  const queryClient = useQueryClient();
  const { mutate, isError, error, isLoading, isSuccess, isPending } =
    useMutation({
      mutationFn: (id) => api.delete(`${API_ROUTES.GET_NETWORKS}/${id}`),
      onSuccess: () => {
        showToast("success", "Network deleted successfully");
      },
      // A network with organizations still attached is refused with a 409, and
      // the message names how many — surface it instead of failing silently.
      onError: (error) => {
        showToast(
          "error",
          error?.response?.data?.message || "Failed to delete network",
        );
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [API_ROUTES.GET_NETWORKS],
        });
      },
    });

  return { mutate, isError, error, isLoading, isSuccess, isPending };
};
